/**
 * Phase 11.5b — Supersede modal for a Strategy Memory entry.
 *
 * RBAC: parent gates render on `auth.hasPermission("strategy_memory.write")`.
 *
 * Fields:
 *   - claim (textarea, required, ≤500 chars, live counter)
 *   - confidence (slider 0-1 step 0.05, required, numeric label)
 *   - evidence_agent_run_ids (comma-separated, optional → array on submit)
 *   - claim_structured (JSON textarea, optional, zod-validated on submit)
 *
 * On submit → POST /api/v1/strategy-memory/entries/{id}/supersede with
 *   `{new_claim, new_confidence, evidence_agent_run_ids, new_claim_structured}`.
 * Success → toast + close + `onSuperseded(newEntry)`.
 * JSON parse error → inline red text near the textarea, modal stays open.
 * HTTP failure → `showAgentError` (429/502/504 aware) + modal stays open.
 */
import { useMemo, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { showAgentError } from "./AgentErrorToast";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../ui/dialog";

const COPY = {
  de: {
    title: "Eintrag ersetzen",
    subtitle:
      "Ersetzt den validierten Eintrag durch eine neue Version. " +
      "Der alte Eintrag wird auf status=superseded gesetzt.",
    claim: "Neue Aussage",
    claim_ph:
      "z. B. Wuppertal Eigentümer 55+ konvertieren 2.7× besser mit Adress-Anker (aktualisiert nach Kampagne Q1/2026).",
    confidence: "Konfidenz",
    evidence: "Evidenz — Agent Run IDs (komma-getrennt)",
    evidence_ph: "run-abc-123, run-def-456",
    claim_structured: "Strukturierte Aussage (optional, JSON)",
    claim_structured_ph:
      '{"channel":"linkedin","hook_type":"rhetorical_question"}',
    json_error: "Ungültiges JSON — bitte Syntax prüfen.",
    claim_required: "Aussage darf nicht leer sein.",
    claim_too_long: (n) => `Aussage darf maximal 500 Zeichen haben (aktuell ${n}).`,
    submit: "Ersetzen",
    submitting: "Ersetze…",
    cancel: "Abbrechen",
    success: "Eintrag erfolgreich ersetzt.",
    chars: (n) => `${n} / 500`,
  },
  en: {
    title: "Supersede entry",
    subtitle:
      "Replaces the validated entry with a new version. The old entry " +
      "is set to status=superseded.",
    claim: "New claim",
    claim_ph:
      "e.g. Wuppertal owners 55+ convert 2.7× better with address anchor (updated after Q1/2026 campaign).",
    confidence: "Confidence",
    evidence: "Evidence — agent run IDs (comma-separated)",
    evidence_ph: "run-abc-123, run-def-456",
    claim_structured: "Structured claim (optional, JSON)",
    claim_structured_ph:
      '{"channel":"linkedin","hook_type":"rhetorical_question"}',
    json_error: "Invalid JSON — please fix the syntax.",
    claim_required: "Claim cannot be empty.",
    claim_too_long: (n) => `Claim can be at most 500 characters (currently ${n}).`,
    submit: "Supersede",
    submitting: "Superseding…",
    cancel: "Cancel",
    success: "Entry superseded successfully.",
    chars: (n) => `${n} / 500`,
  },
};

// Zod schema for the JSON textarea. Empty string → no override (backend
// falls back to the old `claim_structured`). Non-empty must parse to an
// object.
const jsonObjectString = z
  .string()
  .refine((s) => {
    if (!s.trim()) return true;
    try {
      const v = JSON.parse(s);
      return v !== null && typeof v === "object" && !Array.isArray(v);
    } catch { return false; }
  }, "invalid_json");

export function SupersedeModal({ open, onOpenChange, entry, locale = "de",
                                 onSuperseded }) {
  const t = COPY[locale] || COPY.en;
  const [claim, setClaim] = useState("");
  const [confidence, setConfidence] = useState(0.9);
  const [evidence, setEvidence] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset internal state when the modal reopens on a different entry.
  const key = entry?.id || "";
  useMemo(() => {
    setClaim(""); setConfidence(0.9); setEvidence("");
    setJsonText(""); setJsonError(false); setClaimError(null);
    setSubmitting(false);
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, open]);

  const charCount = claim.length;

  async function submit() {
    setClaimError(null);
    setJsonError(false);
    if (!claim.trim()) { setClaimError(t.claim_required); return; }
    if (charCount > 500) { setClaimError(t.claim_too_long(charCount)); return; }

    const parsed = jsonObjectString.safeParse(jsonText);
    if (!parsed.success) { setJsonError(true); return; }

    const body = {
      new_claim: claim.trim(),
      new_confidence: Number(confidence),
      evidence_agent_run_ids: evidence
        .split(",").map((s) => s.trim()).filter(Boolean),
    };
    if (jsonText.trim()) {
      body.new_claim_structured = JSON.parse(jsonText);
    }

    setSubmitting(true);
    try {
      const r = await api.post(
        `/strategy-memory/entries/${entry.id}/supersede`, body);
      toast.success(t.success);
      onSuperseded && onSuperseded(r.data);
      onOpenChange(false);
    } catch (e) {
      showAgentError({ err: e, locale });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="sm-supersede-modal"
        className="sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle>{t.title}</DialogTitle>
          <DialogDescription>{t.subtitle}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Claim */}
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="sm-sup-claim">{t.claim}</Label>
              <span
                data-testid="sm-supersede-char-count"
                className={`text-xs tabular-nums ${
                  charCount > 500 ? "text-rose-600" : "text-muted-foreground"
                }`}
              >
                {t.chars(charCount)}
              </span>
            </div>
            <Textarea
              id="sm-sup-claim"
              data-testid="sm-supersede-claim"
              value={claim}
              onChange={(e) => setClaim(e.target.value)}
              placeholder={t.claim_ph}
              rows={3}
            />
            {claimError && (
              <p className="text-xs text-rose-600"
                 data-testid="sm-supersede-claim-error">{claimError}</p>
            )}
          </div>

          {/* Confidence slider */}
          <div className="space-y-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="sm-sup-conf">{t.confidence}</Label>
              <span
                data-testid="sm-supersede-conf-value"
                className="text-xs tabular-nums text-muted-foreground"
              >
                {Number(confidence).toFixed(2)}
              </span>
            </div>
            <input
              id="sm-sup-conf"
              data-testid="sm-supersede-confidence"
              type="range" min="0" max="1" step="0.05"
              value={confidence}
              onChange={(e) => setConfidence(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          {/* Evidence IDs */}
          <div className="space-y-1.5">
            <Label htmlFor="sm-sup-evidence">{t.evidence}</Label>
            <Textarea
              id="sm-sup-evidence"
              data-testid="sm-supersede-evidence"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              placeholder={t.evidence_ph}
              rows={2}
              className="font-mono text-xs"
            />
          </div>

          {/* Structured JSON */}
          <div className="space-y-1.5">
            <Label htmlFor="sm-sup-json">{t.claim_structured}</Label>
            <Textarea
              id="sm-sup-json"
              data-testid="sm-supersede-structured"
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              placeholder={t.claim_structured_ph}
              rows={3}
              className={
                "font-mono text-xs " +
                (jsonError ? "border-rose-500 focus-visible:ring-rose-500" : "")
              }
            />
            {jsonError && (
              <p className="text-xs text-rose-600"
                 data-testid="sm-supersede-json-error">{t.json_error}</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="sm-supersede-cancel"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >{t.cancel}</Button>
          <Button
            data-testid="sm-supersede-submit"
            onClick={submit}
            disabled={submitting}
          >
            {submitting ? t.submitting : t.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SupersedeModal;
